/** @jsx jsx */
import { jsx, css } from '@emotion/react';
import { Component, FormEvent, MouseEvent } from 'react';
import Content from './forgot-password-submit.content';
import {
  getContent,
  testPassword,
  PWReqsMessage,
  buildErrorMsgFromForm,
  stopProp,
} from '../../utils';
import { Button } from '../../_shared/button';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import Logo from '../../_shared/logo';
import { Alert, message } from 'antd';
import styles from '../authentication.styles';
import SharedStyles from '../../_shared/styles';
import Link from '../../_shared/link';
import { get } from 'lodash';
import { WithForm } from '../../_shared/form';
import { validateCode } from '../../_shared/services/manage-users.service';

const MATCH_ERROR_COLOR = '#ff5630';

interface IProps extends RouteComponentProps {
  forgotPasswordSubmit: (
    email: string,
    code: string,
    password: string
  ) => Promise<void>;
  signIn: (un: string, pw: string, checkrole?: boolean) => Promise<boolean>;
  form?: any;
}

export class FGPasswordComponent extends Component<IProps> {
  state = {
    loading: false,
    error: null,
    // AER-1655: surface an in-context "Passwords must match." error below the
    // Confirm field. Lower priority than the length rule (which AntD renders
    // on the field), so we only flag once both are long enough.
    pwMismatch: false,
  };
  // AER-1655: validate New/Confirm match on blur when both are populated and
  // satisfy the min-length rule.
  checkMatch = () => {
    const { getFieldValue } = this.props.form;
    const pw1 = getFieldValue('pw1') || '';
    const pw2 = getFieldValue('pw2') || '';

    const lengthOk = pw1.length >= 8 && pw2.length >= 8;
    const pwMismatch = !!pw1 && !!pw2 && lengthOk && pw1 !== pw2;
    if (pwMismatch !== this.state.pwMismatch) {
      this.setState({ pwMismatch });
    }
  };
  toDisplay = () => [
    {
      title: Content().codeLabel,
      var: 'code',
      validateTrigger: 'onBlur',
      rules: [
        {
          required: true,
          message: 'Please input the code we sent to your email.',
        },
        {
          max: 10,
          message: 'Code is limited to 10 characters. ',
        },
      ],
    },
    {
      title: Content().passwordLabel,
      type: 'password',
      var: 'pw1',
      validateTrigger: 'onBlur',
      rules: [
        {
          required: true,
          message: 'Please input your password!',
        },
        {
          max: 60,
          message: 'Password is limited to 60 characters. ',
        },
        {
          min: 8,
          message: 'Passwords must be at least 8 characters.',
        },
      ],
      props: {
        onBlur: this.checkMatch,
      },
    },
    {
      title: Content().confirmPasswordLabel,
      var: 'pw2',
      type: 'password',
      validateTrigger: 'onBlur',
      rules: [
        {
          required: true,
          message: 'Please confirm your password!',
        },
        {
          max: 60,
          message: 'Password is limited to 60 characters. ',
        },
        {
          min: 8,
          message: 'Passwords must be at least 8 characters.',
        },
      ],
      props: {
        onBlur: this.checkMatch,
      },
      subtext: () =>
        this.state.pwMismatch ? (
          <div css={css(`color: ${MATCH_ERROR_COLOR}; font-size: 14px;`)}>
            Passwords must match.
          </div>
        ) : null,
    },
  ];
  goToLogin = () => {
    this.props.history.push('/login');
  };
  submit = async (e: MouseEvent | FormEvent) => {
    stopProp(e);
    if (this.state.loading) {
      return;
    }

    this.state.loading = true;
    this.setState(this.state);

    this.props.form.validateFieldsAndScroll(async (err: any, values: any) => {
      if (err) {
        const error = buildErrorMsgFromForm(err);
        message.error(error);
        return this.setState({ loading: false });
      }
      const { code, pw1, pw2 } = values;

      if (!testPassword(pw1)) {
        message.error(PWReqsMessage);
        return this.setState({
          loading: false,
        });
      }

      // AER-1655: block submit if New/Confirm don't match.
      if (pw1 !== pw2) {
        this.setState({ pwMismatch: true, loading: false });
        return;
      }

      const { email } = this.props.history.location.state as any;

      // AER-1654: verify the entered code against the server-side pin store
      // (mirrors the 2FA verify flow) rather than a client-held pin. This is
      // the same gate the reset endpoint enforces; checking here lets us show
      // a clear "code doesn't match" message before attempting the reset.
      const codeValid = await validateCode(email, code);
      if (!codeValid) {
        message.error('Confirmation code does not match');
        return this.setState({
          loading: false,
        });
      }

      try {
        await this.props.forgotPasswordSubmit(email, code, pw1);
        await this.props.signIn(email, pw1, false);
        this.props.history.push(get(this, 'props.location.state.destination', '/dashboard'));
      } catch (err) {
        // AER-1654: an orphaned app user (DB row exists but no Cognito user)
        // self-heals server-side now; any residual failure should surface a
        // clear, actionable message rather than a raw "User does not exist."
        const raw = get(err, 'message', '') as string;
        message.error(
          /user.*not.*exist|does not exist|usernotfound/i.test(raw)
            ? 'We could not complete your password reset. Please contact your administrator for assistance.'
            : raw || 'We could not complete your password reset. Please try again or contact your administrator.'
        );
        this.setState({
          loading: false,
        });
      }
    });
  };
  render() {
    const { getFieldValue, Form } = this.props.form;
    const { error, loading } = this.state;
    const btnDisabled = !(
      getFieldValue('code') &&
      (getFieldValue('pw1') !== '' &&
        getFieldValue('pw1') === getFieldValue('pw2'))
    );
    const content = getContent(Content);

    return (
      <div className="ForgotPasswordSubmit" css={css(styles.container)}>
        <div css={css(SharedStyles.logoWrap)}>
          <Logo />
        </div>

        <p css={css(styles.title)}>{content.title}</p>
        <p css={css(styles.subTitle)}>{content.subTitle}</p>

        <div css={css(styles.inputContainer)}>
          <Form
            items={this.toDisplay()}
            forceUpdate={this}
            layout="vertical"
            onSubmit={this.submit}
          >
            <div css={css(styles.buttonContainer + `margin-top: 20px;`)}>
              <Button
                loading={loading}
                title={content.button}
                onClick={this.submit}
                disabled={btnDisabled}
              />

              <Link
                css={css`
                  margin-top: 40px;
                `}
                onClick={this.goToLogin}
              >
                {content.link}
              </Link>
            </div>
          </Form>
        </div>
      </div>
    );
  }
}

export const ForgotPasswordSubmit = WithForm(withRouter(FGPasswordComponent));
